import { Alert, Box, Button, Chip, LinearProgress, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { Pagination } from '@mui/material';
import { useEffect, useId, useRef, useState } from 'react';

import { SeedDataImportStatus } from '../../graphql/__generated__/graphql';
import { EmptyState } from '../EmptyState';
import { LoadingState } from '../LoadingState';
import { PageHeader } from '../PageHeader';
import {
  formatSeedDataBytes,
  formatSeedDataNumber,
  formatSeedDataTimestamp,
  getSeedDataFileValidationMessage,
  getSeedDataStatusPresentation,
} from './seedDataPresentation';
import {
  SEED_DATA_HISTORY_PAGE_SIZE,
  useSeedDataImports,
  type SeedDataActiveImport,
  type UseSeedDataImportsOptions,
} from './useSeedDataImports';
import type { SeedDataImport } from './seedDataApi';

export type SeedDataPageProps = UseSeedDataImportsOptions;

const Counter = ({ label, value }: { label: string; value: number }) => (
  <Box>
    <Typography component="dt" variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography component="dd" variant="body2" sx={{ m: 0, fontWeight: 600 }}>
      {formatSeedDataNumber(value)}
    </Typography>
  </Box>
);

type SeedDataOutcomes = NonNullable<SeedDataImport['householdOutcomes']>;

const hasSeedDataOutcomes = (
  outcomes: SeedDataOutcomes | null | undefined,
): outcomes is SeedDataOutcomes =>
  outcomes !== null &&
  outcomes !== undefined &&
  Number.isFinite(outcomes.created) &&
  Number.isFinite(outcomes.updated) &&
  Number.isFinite(outcomes.existingUnchanged) &&
  Number.isFinite(outcomes.total);

const OutcomeValues = ({ outcomes }: { outcomes: SeedDataOutcomes | null | undefined }) => {
  if (!hasSeedDataOutcomes(outcomes)) {
    return (
      <Typography aria-label="Not available" component="span" variant="body2">
        —
      </Typography>
    );
  }

  return (
    <Box component="span" sx={{ display: 'grid', gap: 0.25 }}>
      <Typography component="span" variant="body2">Created: {formatSeedDataNumber(outcomes.created)}</Typography>
      <Typography component="span" variant="body2">Updated: {formatSeedDataNumber(outcomes.updated)}</Typography>
      <Typography component="span" variant="body2">Existing: {formatSeedDataNumber(outcomes.existingUnchanged)}</Typography>
    </Box>
  );
};

const OutcomeCounter = ({ label, outcomes }: {
  label: string;
  outcomes: SeedDataOutcomes | null | undefined;
}) => (
  <Box>
    <Typography component="dt" variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Box component="dd" sx={{ m: 0, fontWeight: 600 }}>
      <OutcomeValues outcomes={outcomes} />
    </Box>
  </Box>
);

const ImportStatus = ({ status }: { status: SeedDataImportStatus }) => {
  const presentation = getSeedDataStatusPresentation(status);
  return <Chip color={presentation.color} label={presentation.label} size="small" variant="outlined" />;
};

const ActiveImportPanel = ({ activeImport, onCancel, disabled }: {
  activeImport: SeedDataActiveImport;
  onCancel: (id: string) => void;
  disabled: boolean;
}) => {
  const importRow = activeImport.import;
  const pendingUpload = activeImport.kind === 'pending-upload' ? activeImport.import : null;
  const importedHistory = activeImport.kind === 'import' ? activeImport.import : null;
  const progress = pendingUpload !== null && pendingUpload.sizeBytes > 0
    ? (pendingUpload.loadedBytes / pendingUpload.sizeBytes) * 100
    : null;
  const counters = importedHistory ?? {
    processedRows: 0,
    importedRows: 0,
    skippedRows: 0,
    warningRows: 0,
    attempts: 0,
  };

  return (
    <Paper component="section" variant="outlined" aria-label="Active seed data import" sx={{ mb: 3, p: { xs: 2, sm: 3 } }}>
      <Stack spacing={2}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Box>
            <Typography component="h2" variant="h6" sx={{ fontWeight: 700 }}>
              Active import
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {importRow.originalFilename} · {formatSeedDataBytes(importRow.sizeBytes)}
            </Typography>
          </Box>
          <ImportStatus status={importRow.status} />
        </Box>
        {pendingUpload !== null && progress !== null && (
          <Box>
            <LinearProgress
              aria-label="Seed data upload progress"
              aria-valuemax={pendingUpload.sizeBytes}
              aria-valuemin={0}
              aria-valuenow={pendingUpload.loadedBytes}
              aria-valuetext={`${formatSeedDataBytes(pendingUpload.loadedBytes)} of ${formatSeedDataBytes(pendingUpload.sizeBytes)} uploaded`}
              value={progress}
              variant="determinate"
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {formatSeedDataBytes(pendingUpload.loadedBytes)} of {formatSeedDataBytes(pendingUpload.sizeBytes)} uploaded
            </Typography>
          </Box>
        )}
        <Box component="dl" sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(112px, 1fr))', gap: 2, m: 0 }}>
          <Counter label="Processed" value={counters.processedRows} />
          <Counter label="Imported" value={counters.importedRows} />
          <Counter label="Skipped" value={counters.skippedRows} />
          <Counter label="Warnings" value={counters.warningRows} />
          <OutcomeCounter label="Household outcomes" outcomes={importRow.householdOutcomes} />
          <OutcomeCounter label="Voter outcomes" outcomes={importRow.voterOutcomes} />
          <Counter label="Attempts" value={counters.attempts} />
        </Box>
        {importRow.status === SeedDataImportStatus.AwaitingUpload && (
          <Box>
            <Button disabled={disabled} onClick={() => onCancel(importRow.id)} variant="outlined">
              Cancel upload
            </Button>
          </Box>
        )}
      </Stack>
    </Paper>
  );
};

export const SeedDataPage = (props: SeedDataPageProps) => {
  const fileInputId = useId();
  const errorRef = useRef<HTMLDivElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const seedData = useSeedDataImports(props);
  const errorMessage = fileError ?? seedData.errorMessage;
  const pageCount = Math.max(1, Math.ceil(seedData.total / SEED_DATA_HISTORY_PAGE_SIZE));
  const rangeStart = seedData.total === 0 ? 0 : seedData.page * SEED_DATA_HISTORY_PAGE_SIZE + 1;
  const rangeEnd = Math.min(seedData.total, (seedData.page + 1) * SEED_DATA_HISTORY_PAGE_SIZE);

  useEffect(() => {
    if (fileError !== null || seedData.errorFocusToken > 0) errorRef.current?.focus();
  }, [fileError, seedData.errorFocusToken]);

  const validateSelection = (file: File | null): boolean => {
    const validationMessage = getSeedDataFileValidationMessage(file);
    setFileError(validationMessage);
    return validationMessage === null;
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.currentTarget.files?.[0] ?? null;
    if (!validateSelection(file)) {
      setSelectedFile(null);
      return;
    }
    setSelectedFile(file);
  };

  const handleUpload = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (!validateSelection(selectedFile) || selectedFile === null) return;
    void seedData.upload(selectedFile);
  };

  const controlsDisabled = !seedData.canStartUpload;
  const busy = seedData.userOperation !== null;
  const cancelDisabled = busy && seedData.userOperation !== 'upload';

  return (
    <>
      <PageHeader title="Seed Data" />
      <Box aria-busy={busy} component="section" aria-label="Seed data imports">
        {errorMessage !== null && (
          <Alert ref={errorRef} role="alert" severity="error" tabIndex={-1} sx={{ mb: 3 }}>
            {errorMessage}
          </Alert>
        )}
        <Paper component="section" variant="outlined" aria-labelledby="seed-data-upload-heading" sx={{ mb: 3, p: { xs: 2, sm: 3 } }}>
          <Stack component="form" noValidate onSubmit={handleUpload} spacing={2}>
            <Box>
              <Typography component="h2" id="seed-data-upload-heading" variant="h6" sx={{ fontWeight: 700 }}>
                Upload seed data
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Upload one XLSX workbook smaller than 5 GiB. Only one import can be active at a time.
              </Typography>
            </Box>
            <Box>
              <Typography component="label" htmlFor={fileInputId} sx={{ display: 'block', fontWeight: 600, mb: 0.5 }}>
                Seed data XLSX file
              </Typography>
              <input
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                aria-describedby={selectedFile === null ? undefined : `${fileInputId}-selection`}
                disabled={controlsDisabled}
                id={fileInputId}
                onChange={handleFileChange}
                type="file"
              />
              {selectedFile !== null && (
                <Typography id={`${fileInputId}-selection`} sx={{ mt: 0.5 }} variant="body2">
                  Selected: {selectedFile.name} ({formatSeedDataBytes(selectedFile.size)})
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Button disabled={controlsDisabled} type="submit" variant="contained">
                Upload XLSX
              </Button>
              <Button disabled={busy} onClick={() => void seedData.refresh()} variant="outlined">
                Refresh history
              </Button>
            </Box>
          </Stack>
        </Paper>

        {seedData.activeImport !== null && (
          <ActiveImportPanel
            activeImport={seedData.activeImport}
            disabled={cancelDisabled}
            onCancel={id => void seedData.cancel(id)}
          />
        )}

        <Paper component="section" variant="outlined" aria-label="Seed data import history">
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1, px: 2, pt: 2 }}>
            <Typography component="h2" variant="h6" sx={{ fontWeight: 700 }}>
              Import history
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {seedData.total === 0 ? 'No imports' : `${rangeStart}–${rangeEnd} of ${seedData.total}`}
            </Typography>
          </Box>
          {seedData.loading && seedData.items.length === 0 ? (
            <LoadingState message="Loading seed data import history…" />
          ) : seedData.items.length === 0 ? (
            <EmptyState title="No seed data imports yet" hint="Upload an XLSX workbook to begin importing seed data." />
          ) : (
            <TableContainer sx={{ overflowX: 'auto', mt: 2 }}>
              <Table size="small" sx={{ minWidth: 1420 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>File</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell align="right">Processed</TableCell>
                    <TableCell align="right">Imported</TableCell>
                    <TableCell align="right">Skipped</TableCell>
                    <TableCell align="right">Warnings</TableCell>
                    <TableCell align="right">Household outcomes</TableCell>
                    <TableCell align="right">Voter outcomes</TableCell>
                    <TableCell align="right">Attempts</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {seedData.items.map(item => (
                    <TableRow hover key={item.id}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {item.originalFilename}
                        </Typography>
                        <Typography color="text.secondary" variant="caption">
                          {formatSeedDataBytes(item.sizeBytes)}
                        </Typography>
                      </TableCell>
                      <TableCell><ImportStatus status={item.status} /></TableCell>
                      <TableCell>{formatSeedDataTimestamp(item.createdAt)}</TableCell>
                      <TableCell align="right">{formatSeedDataNumber(item.processedRows)}</TableCell>
                      <TableCell align="right">{formatSeedDataNumber(item.importedRows)}</TableCell>
                      <TableCell align="right">{formatSeedDataNumber(item.skippedRows)}</TableCell>
                      <TableCell align="right">{formatSeedDataNumber(item.warningRows)}</TableCell>
                      <TableCell align="right"><OutcomeValues outcomes={item.householdOutcomes} /></TableCell>
                      <TableCell align="right"><OutcomeValues outcomes={item.voterOutcomes} /></TableCell>
                      <TableCell align="right">{formatSeedDataNumber(item.attempts)}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap' }}>
                          {item.status === SeedDataImportStatus.AwaitingUpload && (
                            <Button disabled={cancelDisabled} onClick={() => void seedData.cancel(item.id)} size="small">
                              Cancel upload
                            </Button>
                          )}
                          {item.status === SeedDataImportStatus.Failed && (
                            <Button disabled={busy} onClick={() => void seedData.retry(item.id)} size="small">
                              Retry
                            </Button>
                          )}
                          {item.errorReportAvailable && (
                            <Button disabled={busy} onClick={() => void seedData.downloadErrors(item.id)} size="small">
                              Download errors
                            </Button>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <Pagination
              count={pageCount}
              disabled={busy || seedData.loading}
              onChange={(_event, nextPage) => seedData.setPage(nextPage - 1)}
              page={Math.min(seedData.page + 1, pageCount)}
              shape="rounded"
              showFirstButton
              showLastButton
              size="small"
            />
          </Box>
        </Paper>
        <Box aria-atomic="true" aria-live="polite" role="status" sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
          {seedData.announcement}
        </Box>
      </Box>
    </>
  );
};
